import LoadingAnimation from '@/components/LoadingAnimation/LoadingAnimation'
import DeviceSuggestionCard from '@/app/pages/utility-program/unenroll/suggestions/components/DeviceSuggestionCard'
import { CostCategoryPreviewRow } from '@/app/pages/utility-program/unenroll/suggestions/components/ThermostatSuggestionCard/ThermostatSuggestionCard.components'
import Separator from '@/app/components/Separator/Separator'
import { Text } from '@/components'
import ConfigureDeviceStepBody from '../components/ConfigureDeviceStepBody'
import ProgramPrescribedAlert from '../components/ProgramPrescribedAlert'
import useConfigureThermostatViewModel from './ConfigureThermostat.viewmodel'

const { SettingsSuggestions, SuggestionCard, SuggestionDescription } =
  DeviceSuggestionCard

export default function ConfigureThermostat() {
  const {
    isLoadingOrIdle,
    displayName,
    settingPaths,
    costCategories,
    inactiveTimesSubtitleLines,
    gridSupportEnabled,
    gridSupportSummary,
    precoolEnabled,
    saveMoneyPrescribed,
    saveMoneyEnabled,
  } = useConfigureThermostatViewModel()

  if (isLoadingOrIdle) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingAnimation type="falling" />
      </div>
    )
  }

  return (
    <ConfigureDeviceStepBody>
      <DeviceSuggestionCard
        iconName="Thermometer"
        title={displayName}
        articles={[]}
      >
        <SettingsSuggestions>
          <SuggestionCard
            iconName="Globe"
            title="Grid Support"
            destinationPath={settingPaths.gridSupport}
          >
            <SuggestionDescription>
              We&apos;ll adjust your temperature based on your set temp range
              for DR events
            </SuggestionDescription>
            {gridSupportEnabled && gridSupportSummary.length > 0 ? (
              <div className="flex flex-col gap-1">
                {gridSupportSummary.map((setting) => (
                  <div key={setting.label ?? setting.description}>
                    {setting.label && (
                      <Text
                        variant="body3"
                        className="font-bold text-themed-grey-500"
                      >
                        {setting.label}
                      </Text>
                    )}
                    <SuggestionDescription>
                      {setting.description}
                    </SuggestionDescription>
                  </div>
                ))}
              </div>
            ) : (
              <SuggestionDescription>Disabled</SuggestionDescription>
            )}
          </SuggestionCard>

          <SuggestionCard
            iconName="Wind"
            title="Pre-cool"
            destinationPath={settingPaths.precool}
          >
            <SuggestionDescription>
              We&apos;ll cool your home down to your desired temperature when
              electricity rates are cheapest
            </SuggestionDescription>
            <SuggestionDescription>
              {precoolEnabled ? 'Enabled' : 'Disabled'}
            </SuggestionDescription>
          </SuggestionCard>

          <SuggestionCard
            iconName="DollarSign"
            title="Save Money"
            destinationPath={settingPaths.costCategories}
          >
            <SuggestionDescription>
              We&apos;ll check rates hourly and shift your temperature based on
              your preferences
            </SuggestionDescription>
            {saveMoneyPrescribed && costCategories.length > 0 ? (
              <>
                <div className="flex flex-col gap-1 mt-2">
                  <CostCategoryPreviewRow
                    type="negative"
                    costCategories={costCategories}
                  />
                  <CostCategoryPreviewRow
                    type="low"
                    costCategories={costCategories}
                  />
                  <CostCategoryPreviewRow
                    type="medium"
                    costCategories={costCategories}
                  />
                  <CostCategoryPreviewRow
                    type="high"
                    costCategories={costCategories}
                  />
                </div>
                <Separator className="my-2 bg-themed-grey-200" />
                <Text
                  variant="body3"
                  className="font-bold text-themed-grey-500"
                >
                  Inactive Times:
                </Text>
                {inactiveTimesSubtitleLines.length > 0 ? (
                  inactiveTimesSubtitleLines.map((line) => (
                    <SuggestionDescription key={line}>
                      {line}
                    </SuggestionDescription>
                  ))
                ) : (
                  <SuggestionDescription>No times set</SuggestionDescription>
                )}
              </>
            ) : (
              <SuggestionDescription>
                {saveMoneyEnabled ? 'Enabled' : 'Disabled'}
              </SuggestionDescription>
            )}
          </SuggestionCard>
          {saveMoneyPrescribed && (
            <ProgramPrescribedAlert settingDisplayName="Save Money" />
          )}
        </SettingsSuggestions>
      </DeviceSuggestionCard>
    </ConfigureDeviceStepBody>
  )
}
